import { getServices } from './matchServices';
import { ServiceName } from './Reasons';
import { Service } from './Service';

/**
 *
 * @param service
 * @returns {boolean}
 * this tells us if a particular service has any known prices
 */
export function isUnknownPrice(service?: Service) {
  // min_price, max_price, price are all strings, just checking they are not empty
  if (service?.details?.has_price_range) {
    return !service.min_price || !service.max_price;
  }

  return !service?.price;
}

export const isNonBookable = ({ name }: Service) =>
  name === ServiceName['x-ray_chest'] ||
  name === ServiceName['x-ray_extremity'] ||
  name === ServiceName.laceration_repair;

export const isBaseRequired = ({ details }: Service) =>
  Boolean(details?.requires_base_office_visit_fee);

/**
 * priceWithBase: the price range with the base office fee added in
 * priceWithoutBase: the price range without the base office fee added in
 * baseOfficeVisitFee: we return the office fee if one service requires it and it has a defined price
 * isVaries: true if some prices are known and some are unknown
 * isAllUnknown: true if all prices are unknown for the group of matched reasons
 * isUnknownOfficeFeeAndNeeded: true if at least one service requires the base office visit fee, but it does not have a price
 * isBaseOfficeVisitRequired: true if the visit is required regardless of whether pricing info is available
 */
export type SelfPayRowData = {
  priceWithBase: [number, number] | null;
  priceWithoutBase: [number, number] | null;
  baseOfficeVisitFee: [number, number] | null;
  isUnknownOfficeFeeAndNeeded: boolean;
  isVaries: boolean;
  isAllUnknown: boolean;
  isBaseOfficeVisitRequired: boolean;
};

/**
 * Gets an array of Services for a given array of ServiceNames
 */
export const getMatchedServices = (
  servicesObj: Record<string, Service[]>,
  matchedReasons: readonly ServiceName[] = []
) => {
  const locationServices = getServices(servicesObj);

  const matchedServices = matchedReasons
    .filter((reason) => {
      const service = locationServices[reason];
      return service && service.details?.accept_cash;
    })
    .map((reason) => locationServices[reason]);

  return {
    matchedServices,
    locationServices,
  };
};

/**
 *
 * @param servicesObj
 * @param matchedReasons
 * @returns {SelfPayRowData}
 */
export const getSelfPayRowData = (
  servicesObj: Record<string, Service[]>,
  matchedReasons: readonly ServiceName[]
): SelfPayRowData => {
  let isBaseVisitPriceNeededGlobal = false;
  let isBaseVisitPriceUnknown = false;

  let knownServicesCount = 0;

  let priceWithBase: [number, number] | null = null;
  let priceWithoutBase: [number, number] | null = null;

  const { matchedServices, locationServices } = getMatchedServices(servicesObj, matchedReasons);

  const baseOfficeVisit = locationServices.base_office_visit;

  // base office fees min/max
  let effectiveBaseOfficeRange: [number, number] = [0, 0];
  if (isUnknownPrice(baseOfficeVisit)) {
    isBaseVisitPriceUnknown = true;
  } else {
    if (
      baseOfficeVisit?.details?.has_price_range &&
      baseOfficeVisit.min_price &&
      baseOfficeVisit.max_price
    ) {
      effectiveBaseOfficeRange = [
        parseFloat(baseOfficeVisit.min_price),
        parseFloat(baseOfficeVisit.max_price),
      ];
    } else if (baseOfficeVisit.price) {
      effectiveBaseOfficeRange = [
        parseFloat(baseOfficeVisit.price),
        parseFloat(baseOfficeVisit.price),
      ];
    }
  }

  // actual prices
  matchedServices.forEach((service) => {
    let isBaseVisitPriceNeededLocal = false;

    // base price
    if (service?.details?.requires_base_office_visit_fee) {
      isBaseVisitPriceNeededLocal = true;
      isBaseVisitPriceNeededGlobal = true;
    }

    if (!isUnknownPrice(service)) {
      // we know that by getting here we have either min/max or price
      // need to initialize these to numbers for the compiler
      let localMinPrice = 0;
      let localMaxPrice = 0;

      knownServicesCount += 1;

      if (service.details?.has_price_range && service.min_price && service.max_price) {
        localMinPrice = parseFloat(service.min_price);
        localMaxPrice = parseFloat(service.max_price); //todo: protect isNAN && cent math
      } else if (service.price) {
        localMinPrice = parseFloat(service.price);
        localMaxPrice = localMinPrice;
      }

      priceWithoutBase = [
        priceWithoutBase ? Math.min(localMinPrice, priceWithoutBase[0]) : localMinPrice,
        priceWithoutBase ? Math.max(localMaxPrice, priceWithoutBase[1]) : localMaxPrice,
      ];

      priceWithBase = priceWithBase
        ? [
            Math.min(
              localMinPrice + (isBaseVisitPriceNeededLocal ? effectiveBaseOfficeRange[0] : 0),
              priceWithBase[0]
            ),
            Math.max(
              localMaxPrice + (isBaseVisitPriceNeededLocal ? effectiveBaseOfficeRange[1] : 0),
              priceWithBase[1]
            ),
          ]
        : [
            localMinPrice + (isBaseVisitPriceNeededLocal ? effectiveBaseOfficeRange[0] : 0),
            localMaxPrice + (isBaseVisitPriceNeededLocal ? effectiveBaseOfficeRange[1] : 0),
          ];
    }
  });

  return {
    priceWithBase,
    priceWithoutBase,
    isBaseOfficeVisitRequired: isBaseVisitPriceNeededGlobal,
    baseOfficeVisitFee:
      isBaseVisitPriceNeededGlobal && !isBaseVisitPriceUnknown ? effectiveBaseOfficeRange : null, // (known and needed)
    isUnknownOfficeFeeAndNeeded: isBaseVisitPriceNeededGlobal && isBaseVisitPriceUnknown, // (unknown and needed)
    isVaries: knownServicesCount > 0 && knownServicesCount < matchedServices.length,
    isAllUnknown: knownServicesCount === 0,
  };
};
